/**
 * Deciding how a stem should be declined.
 *
 * Sanskrit declension is not a function of spelling. `brahman` is *brahmā* as a
 * masculine but *brahma* as a neuter; `pitṛ` takes *pitaram* where `kartṛ` takes
 * *kartāram*; `bhū` as a verbal root gives *bhuvam* where a plain stem would give
 * *bhūm*. The engine can derive all of these correctly, but only if it is told
 * what kind of word it is looking at.
 *
 * This module assembles that lexical description from three sources, most
 * specific first:
 *
 *   1. A curated exception lexicon, for words whose analysis nothing else records.
 *   2. Markup already present in the Monier-Williams entry the app has loaded —
 *      `<info lex="m:f#A:n">` carries gender *and* how the feminine is formed,
 *      `verb="genuineroot"` marks verbal roots, `lexcat="LEXID=pron"` marks
 *      pronouns. This costs no extra bundle weight; the data is already on screen.
 *   3. Shape heuristics, which are correct for the large regular majority.
 */

import { EXCEPTION_LEXICON, LexiconEntry } from "./lexicon";
import { StemAnalysis } from "./vidyut-engine";
import { Gender } from "./types";

/** Grammatical facts recovered from a dictionary entry. */
export type LexicalHints = {
    /** Genders the word is attested in. */
    genders?: Gender[];
    /**
     * How the feminine is formed, from MW's `f#A` / `f#I` notation. A value here
     * means the feminine stem ends in a nyāp affix (ṭāp/ṅīp) and must be declined
     * as such — *sītā*, not *viśvapā*.
     */
    feminineSuffix?: string;
    /** Broad lexical category, where the dictionary states one. */
    category?: "root" | "pronoun" | "numeral" | "participle" | "indeclinable";
    /**
     * For verbal roots, MW's `cp` attribute: the classes and padas the root
     * takes, as `1P`, `1P,1Ā`, `10P`. A headword like *vid* matches five
     * Dhātupāṭha entries which are different verbs, so this is what picks out
     * the intended one.
     */
    verbClasses?: string;
};

/** How confident we are in the analysis, so the UI can be honest with the reader. */
export type Confidence = "attested" | "inferred";

export type AnnotatedStem = {
    analysis: StemAnalysis;
    /** Attested forms for cells the engine gets wrong; see LexiconEntry. */
    overrides?: LexiconEntry["overrides"];
    /** A second licensed reading to derive and merge; see LexiconEntry. */
    alsoAs?: LexiconEntry["alsoAs"];
    confidence: Confidence;
    /** Where the analysis came from, for debugging and for the UI's provenance note. */
    source: "lexicon" | "dictionary" | "heuristic";
    /** Human-readable gloss of the analysis, e.g. "su + √dhī (root-noun)". */
    note?: string;
};

/* ------------------------------------------------ reading the MW entry markup */

const INFO_TAG = /<info\s+([^/>]*)\/?>/g;
const ATTR = /(\w+)="([^"]*)"/g;

/**
 * Pulls grammatical hints out of a Monier-Williams entry body.
 *
 * MW's `lex` attribute is a colon-separated list such as `m:f#A:n`, meaning the
 * word is masculine, forms its feminine with -ā, and is also neuter. `inh` means
 * "inherit from the previous entry" and carries no information on its own.
 */
export const parseMwHints = (entryHtml: string): LexicalHints => {
    const hints: LexicalHints = {};
    const genders = new Set<Gender>();

    INFO_TAG.lastIndex = 0;
    let tag: RegExpExecArray | null;
    while ((tag = INFO_TAG.exec(entryHtml)) !== null) {
        ATTR.lastIndex = 0;
        let attr: RegExpExecArray | null;
        while ((attr = ATTR.exec(tag[1])) !== null) {
            const [, name, value] = attr;

            if (name === "lex" && value !== "inh") {
                for (const part of value.split(":")) {
                    if (part === "ind") hints.category = hints.category ?? "indeclinable";
                    else if (part.startsWith("m")) genders.add("mas");
                    else if (part.startsWith("n")) genders.add("neu");
                    else if (part.startsWith("f")) {
                        genders.add("fem");
                        const hash = part.indexOf("#");
                        if (hash !== -1) hints.feminineSuffix = part.slice(hash + 1);
                    }
                }
            } else if (name === "verb" && value === "genuineroot") {
                hints.category = "root";
            } else if (name === "cp" && value) {
                hints.verbClasses = hints.verbClasses ? `${hints.verbClasses},${value}` : value;
            } else if (name === "lexcat") {
                if (value.includes("LEXID=pron")) hints.category = "pronoun";
                else if (value.includes("LEXID=card")) hints.category = "numeral";
                else if (value.includes("LEXID=prap")) hints.category = "participle";
            }
        }
    }

    if (genders.size > 0) hints.genders = [...genders];
    return hints;
};

/* -------------------------------------------------------------- the decision */

/** Vowels that a nyāp (ṭāp/ṅīp/ūṅ) feminine stem ends in. */
const NYAP_FINALS = ["ā", "ī", "ū"];

/**
 * Decides how to hand `stem` to the engine for the given gender.
 *
 * The curated lexicon wins outright; then the dictionary's own markup; then
 * shape. Anything resolved by shape alone is reported as `inferred` so the UI
 * can mark it rather than silently present a guess as fact.
 */
export const annotate = (
    stem: string,
    gender: Gender,
    hints: LexicalHints = {},
): AnnotatedStem => {
    const entry = EXCEPTION_LEXICON[stem];
    if (entry) {
        const forGender = entry[gender] ?? entry.any;
        if (forGender) {
            return {
                analysis: forGender.analysis,
                confidence: "attested",
                source: "lexicon",
                note: forGender.note,
                overrides: forGender.overrides,
                alsoAs: forGender.alsoAs,
            };
        }
    }

    // NOTE: MW's `genuineroot` flag says the headword is *also* a verbal root, not
    // that its nominal sense declines as a root-noun. An earlier version treated
    // the two as the same and handed the plain stem to the engine as if it were
    // an aupadeśika — but `vid` is not an aupadeśika (`vida~` is), so the engine
    // returned nothing and the whole table came out empty for every such word.
    //
    // Genuine root-nouns that decline differently (bhū, dhī, budh, guh, pur) are
    // in EXCEPTION_LEXICON above with their real aupadeśika. Everything else
    // declines as the ordinary stem it looks like, and the conjugation view
    // handles the verbal reading separately.

    // MW states the feminine suffix explicitly, so we know it is a nyāp stem.
    if (gender === "fem" && hints.feminineSuffix) {
        return {
            analysis: { kind: "nyap", stem },
            confidence: "attested",
            source: "dictionary",
            note: `feminine in -${hints.feminineSuffix}`,
        };
    }

    // Otherwise: a feminine ending in a long vowel is overwhelmingly a ṭāp/ṅīp
    // derivative. The exceptions are root-nouns in -ā/-ī/-ū, which the lexicon
    // above catches.
    if (gender === "fem" && NYAP_FINALS.some(v => stem.endsWith(v))) {
        return {
            analysis: { kind: "nyap", stem },
            confidence: "inferred",
            source: "heuristic",
        };
    }

    return {
        analysis: { kind: "basic", stem },
        confidence: "inferred",
        source: "heuristic",
    };
};

/**
 * Combines the hints from several entries of the same headword.
 *
 * A dictionary headword usually has many entries, and the grammatical
 * information is spread across them: *vid* is flagged as a verbal root in its
 * first and seventh entries and as a noun in between. Taking the last entry's
 * hints would lose the root flag entirely, so they are merged — genders and verb
 * classes accumulate, and a category once established is kept.
 */
export const mergeHints = (a: LexicalHints, b: LexicalHints): LexicalHints => {
    const genders = [...new Set([...(a.genders ?? []), ...(b.genders ?? [])])];
    const classes = [a.verbClasses, b.verbClasses].filter(Boolean).join(",");
    return {
        ...(genders.length ? { genders } : {}),
        // "root" is the most consequential category, so it is never displaced.
        category: a.category === "root" || b.category === "root"
            ? "root"
            : a.category ?? b.category,
        feminineSuffix: a.feminineSuffix ?? b.feminineSuffix,
        ...(classes ? { verbClasses: classes } : {}),
    };
};
